import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import HandoffReport from "@/components/HandoffReport";
import { ArrowLeft, Printer, Zap, Save } from "lucide-react";

export default function AdminProjectHandoff() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    const r = await api.get(`/projects/${id}/handoff`);
    setB(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!b) return <div className="text-sm text-[#94A3B8]">Preparing bundle…</div>;

  return (
    <div data-testid="project-handoff">
      <div className="print:hidden flex items-center justify-between max-w-4xl mx-auto mb-4 gap-3 flex-wrap">
        <Link to={`/admin/projects/${id}`} className="text-xs text-[#C9A356] inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to project
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setEditing(!editing)}
            className="h-9 px-3 rounded-md text-xs font-semibold bg-[#0A2540] text-white hover:bg-[#12365b] inline-flex items-center gap-1"
            data-testid="commissioning-edit-toggle"
          >
            <Zap className="w-3.5 h-3.5" /> {editing ? "Close editor" : "Edit commissioning"}
          </button>
          <button
            onClick={() => window.print()}
            className="btn-accent text-sm inline-flex items-center gap-1"
            data-testid="print-handoff"
          >
            <Printer className="w-4 h-4" /> Print / Save PDF
          </button>
        </div>
      </div>

      {editing && (
        <div className="max-w-4xl mx-auto mb-6">
          <CommissioningEditor
            projectId={id}
            initial={b.commissioning || {}}
            onSaved={() => { setEditing(false); load(); }}
          />
        </div>
      )}

      <HandoffReport bundle={b} mode="admin" />
    </div>
  );
}

function CommissioningEditor({ projectId, initial, onSaved }) {
  const [form, setForm] = useState({
    pv_open_circuit_v:  initial.pv_open_circuit_v || "",
    string_voltage:     initial.string_voltage || "",
    insulation_test:    initial.insulation_test || "",
    earth_bond_ohms:    initial.earth_bond_ohms || "",
    inverter_output_ac: initial.inverter_output_ac || "",
    battery_soc:        initial.battery_soc || "",
    grid_export_test:   initial.grid_export_test || "",
    monitoring_verified: initial.monitoring_verified ?? null,
    notes:              initial.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Strip empty strings so they don't overwrite existing values.
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await api.patch(`/projects/${projectId}/commissioning`, payload);
      toast.success("Commissioning results saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="card-pb p-6 border-2 border-[#C9A356]/40" data-testid="commissioning-editor">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-4 h-4 text-[#C9A356]" />
        <h3 className="font-heading text-lg text-[#0A2540] dark:text-white">
          Commissioning & test results
        </h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="PV open-circuit voltage" unit="V" value={form.pv_open_circuit_v} onChange={set("pv_open_circuit_v")} testid="cf-voc" />
        <Field label="String voltage" unit="V" value={form.string_voltage} onChange={set("string_voltage")} testid="cf-string" />
        <Field label="Insulation resistance" unit="MΩ" value={form.insulation_test} onChange={set("insulation_test")} testid="cf-iso" />
        <Field label="Earth bond" unit="Ω" value={form.earth_bond_ohms} onChange={set("earth_bond_ohms")} testid="cf-earth" />
        <Field label="Inverter AC output" unit="V" value={form.inverter_output_ac} onChange={set("inverter_output_ac")} testid="cf-inverter" />
        <Field label="Battery SOC" unit="%" value={form.battery_soc} onChange={set("battery_soc")} testid="cf-soc" />
        <Field label="Grid export test" value={form.grid_export_test} onChange={set("grid_export_test")} testid="cf-export" />
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#C9A356] font-bold mb-1">
            Monitoring portal
          </label>
          <select
            value={form.monitoring_verified === null ? "" : form.monitoring_verified ? "yes" : "no"}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                monitoring_verified: e.target.value === "" ? null : e.target.value === "yes",
              }))
            }
            data-testid="cf-monitoring"
            className="w-full h-10 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-2 text-sm"
          >
            <option value="">—</option>
            <option value="yes">Verified & live</option>
            <option value="no">Not verified</option>
          </select>
        </div>
      </div>
      <div className="mt-4">
        <label className="block text-[10px] uppercase tracking-wider text-[#C9A356] font-bold mb-1">
          Notes
        </label>
        <textarea
          value={form.notes}
          onChange={set("notes")}
          rows={3}
          data-testid="cf-notes"
          placeholder="Site-specific observations, deviations, thermal readings…"
          className="w-full rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-3 py-2 text-sm"
        />
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="cf-save"
        className="mt-4 h-10 px-4 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50"
      >
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save commissioning"}
      </button>
    </div>
  );
}

function Field({ label, unit, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-[#C9A356] font-bold mb-1">
        {label}{unit ? ` (${unit})` : ""}
      </label>
      <input
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full h-10 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-3 text-sm"
      />
    </div>
  );
}
